// The item panel's "On order": open PO lines for this item (what's still to arrive and when), and
// a quick way to order more from its preferred vendor (added to the open draft for that vendor, ship-to and kind). The
// usual table (it scrolls inside past 12), its View and Columns in the row under the title.
import { useMemo, useState } from "react";
import type { Po, PoLine } from "../../../../shared/db.gen.ts";
import { isOpen, PO_STATUS_LABELS } from "../../../../shared/po.ts";
import { dayText, qty } from "../../lib/format.ts";
import { can } from "../../lib/mutate.ts";
import { usePlan } from "../../lib/planning.ts";
import { navigate } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { Section } from "../../ui/RecordPanel.tsx";
import { PaneLink, usePaneOpen } from "../../ui/SidePane.tsx";
import { toast } from "../../ui/toast.tsx";
import { poPath, STATUS_TONE, useLinesByPo, vendorLabel } from "./lib.ts";
import { NewPoDialog } from "./NewPo.tsx";

type Row = { id: string; line: PoLine; po: Po; left: number; vendor: string; date: string | null };

export function OnOrder({ itemId }: { itemId: string }) {
  const paneOpen = usePaneOpen(); // Side pane --- the draft it was added to opens beside it
  const lines = useRows("po_line");
  const pos = useRows("po");
  const vendors = useRows("vendor");
  const links = useRows("vendor_item");
  const items = useRows("item");
  const _linesByPo = useLinesByPo();
  const plan = usePlan();

  const open = useMemo<Row[]>(() => {
    const poById = new Map(pos.map((p) => [p.id, p]));
    return lines
      .filter((l) => l.item_id === itemId)
      .flatMap((l) => {
        const po = poById.get(l.po_id);
        return po && isOpen(po.status)
          ? [
              {
                id: l.id,
                line: l,
                po,
                left: Math.max(0, Number(l.qty) - Number(l.qty_received)),
                vendor: vendorLabel(vendors.find((v) => v.id === po.vendor_id)),
                date: l.promised_date ?? po.expected_arrive ?? po.expected_ship,
              },
            ]
          : [];
      });
  }, [lines, pos, vendors, itemId]);

  // Still to arrive: what's left on sent POs (a draft isn't coming yet)
  const stillToArrive = (rows: Row[]) => rows.filter((o) => o.po.status !== "draft").reduce((n, o) => n + o.left, 0);
  const columns: Column<Row>[] = [
    {
      key: "po",
      header: "PO",
      width: "84px",
      value: (r) => r.po.number,
      cell: (r) => (
        <PaneLink kind="po" id={r.po.id} to={poPath(r.po.id)} className="font-mono text-[12px] font-medium hover:underline">
          {r.po.number}
        </PaneLink>
      ),
    },
    { key: "vendor", header: "Vendor", width: "minmax(110px,1fr)", cell: (r) => <span className="truncate">{r.vendor}</span> },
    {
      key: "status",
      header: "Status",
      width: "120px",
      value: (r) => PO_STATUS_LABELS[r.po.status],
      cell: (r) => <Badge tone={STATUS_TONE[r.po.status]}>{PO_STATUS_LABELS[r.po.status]}</Badge>,
    },
    {
      key: "date",
      header: "Arrives",
      headerTitle: "The vendor's promised date, else when the PO is needed by, else when it ships",
      width: "84px",
      // Without a date: last
      value: (r) => r.line.promised_date ?? r.po.expected_arrive ?? "9",
      text: (r) => dayText(r.date),
      cell: (r) => <span className="text-[12px] text-muted">{dayText(r.date)}</span>,
    },
    {
      key: "left",
      header: "Left",
      width: "72px",
      align: "right",
      cell: (r) => <span className="font-mono text-[12px]">{qty(r.left)}</span>,
      total: (rows) => (
        <span className="font-mono" title="Still to arrive on sent POs (drafts aren't counted)">
          {qty(stillToArrive(rows))}
        </span>
      ),
    },
  ];

  // Order opens the one New PO dialog with this item (NewPoDialog: its preferred vendor's open draft for where the plan
  // ships it, else a new one, saying which first). Builds go on a run, from the Make plan.
  const [ordering, setOrdering] = useState(false);
  const item = items.find((i) => i.id === itemId);
  const order = () => {
    if (plan.byItem.get(itemId)?.role === "make") {
      toast.info("Builds go on a run: start one from the Make plan");
      return navigate(`/make?item=${itemId}`);
    }
    if (!links.some((l) => l.item_id === itemId && vendors.some((v) => v.id === l.vendor_id && !v.archived)))
      return toast.error("Add a vendor to this item first");
    setOrdering(true);
  };

  return (
    <Section title="On order" count={open.length} addLabel="Order" onAdd={can("po.add_lines") ? order : undefined}>
      {ordering && item && <NewPoDialog items={[item]} onClose={() => setOrdering(false)} />}
      {!open.length ? (
        <div className="px-2 pb-2 text-muted">Not on any open PO.</div>
      ) : (
        <div className="px-2 pb-2">
          <DataTable
            fit
            rows={open}
            columns={columns}
            defaultSort={{ key: "date" }}
            onOpen={(r) => paneOpen("po", r.po.id)}
            exportName="Item on order"
            pageFilters={false}
            rowNoun={["line", "lines"]}
          />
        </div>
      )}
    </Section>
  );
}
